"use client";

import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { createElement, type ReactNode } from "react";
import { ExpandCollapse } from "@/components/ExpandCollapse";
import { RichText } from "@/components/RichText";
import type { ItemProperties, POIPageContent } from "@/types";
import { descriptionSource } from "../../poiDescription";

/** Height in px a collapsed description is clamped to. */
const PREVIEW_HEIGHT = 140;

/**
 * Loaded only when a tenant actually authored a block as Markdown.
 *
 * `react-markdown` brings the whole remark/micromark pipeline with it, and most
 * tenants author these blocks as HTML — which the app can render with no extra
 * parser at all. Paying for it on every page load of every project would be the
 * largest single item in the bundle for a minority feature.
 */
const Markdown = dynamic(() => import("react-markdown"));

/**
 * A free-text block of the POI page.
 *
 * The source is either HTML or Markdown depending on how the tenant authored the
 * field; see `descriptionSource`.
 */
export function Description({
  properties,
  options,
}: {
  properties: ItemProperties;
  options: POIPageContent;
}) {
  const t = useTranslations("poiPage");

  const source = descriptionSource(properties, options);
  if (!source) return null;

  let content: ReactNode = options.isMarkdown ? (
    <Markdown>{source}</Markdown>
  ) : (
    <RichText html={source} />
  );

  // The tenant can wrap the block in an arbitrary tag — how a description is
  // given a `<section>` or an `<article>` without a config field per tag.
  if (options.wrapperTag) content = createElement(options.wrapperTag, null, content);

  if (!options.expandable) return content;

  return (
    <div className="relative mb-[2.5rem]">
      <ExpandCollapse
        previewHeight={PREVIEW_HEIGHT}
        expandOnBodyClick
        expandLabel={
          <>
            <i className="icon ic-jammaster_chevron-down mr-1" />{" "}
            <span>{t("expand")}</span>
          </>
        }
        collapseLabel={
          <>
            <i className="icon ic-jammaster_chevron-up mr-1" />{" "}
            <span>{t("collapse")}</span>
          </>
        }
      >
        {content}
      </ExpandCollapse>
    </div>
  );
}
